import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { getGradeLabel } from '../../api/studentPortalApi';

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const SelectBox = styled.div`
  min-height: 44px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;

  &:focus-within {
    border-color: #F1AF59;
    box-shadow: 0 0 0 3px rgba(241, 175, 89, 0.15);
  }
`;

const SelectedTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #fef3e2;
  border: 1px solid #F1AF59;
  border-radius: 4px;
  font-size: 13px;
  color: #92400e;

  button {
    background: none;
    border: none;
    color: #92400e;
    cursor: pointer;
    padding: 0;
    font-size: 14px;
    line-height: 1;

    &:hover {
      color: #451a03;
    }
  }
`;

const Placeholder = styled.span`
  color: #9ca3af;
  font-size: 14px;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 250px;
  overflow-y: auto;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  margin-top: 4px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
  }
`;

const Option = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${props => props.$selected ? '#fef3e2' : 'white'};

  &:hover {
    background: ${props => props.$selected ? '#fef3e2' : '#f3f4f6'};
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #F1AF59;
`;

const StudentInfo = styled.div`
  flex: 1;

  .name {
    font-weight: 500;
    color: #1f2937;
  }

  .meta {
    font-size: 12px;
    color: #6b7280;
  }
`;

const NoResults = styled.div`
  padding: 12px;
  color: #6b7280;
  font-size: 14px;
  text-align: center;
`;

/**
 * Multi-select student component with search functionality.
 * Used for selecting students when creating a parent user.
 */
const StudentMultiSelect = ({
  students = [],
  selectedIds = [],
  onSelectionChange,
  placeholder = "Select students / 选择学生..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter students by search term
  const filteredStudents = students.filter(student => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      student.name?.toLowerCase().includes(term) ||
      student.schoolName?.toLowerCase().includes(term)
    );
  });

  const toggleStudent = (studentId) => {
    if (selectedIds.includes(studentId)) {
      onSelectionChange(selectedIds.filter(id => id !== studentId));
    } else {
      onSelectionChange([...selectedIds, studentId]);
    }
  };

  const removeStudent = (e, studentId) => {
    e.stopPropagation();
    onSelectionChange(selectedIds.filter(id => id !== studentId));
  };

  const selectedStudents = students.filter(s => selectedIds.includes(s.id));

  return (
    <Container ref={containerRef}>
      <SelectBox onClick={() => setIsOpen(true)}>
        {selectedStudents.length === 0 ? (
          <Placeholder>{placeholder}</Placeholder>
        ) : (
          selectedStudents.map(student => (
            <SelectedTag key={student.id}>
              {student.name}
              <button onClick={(e) => removeStudent(e, student.id)}>&times;</button>
            </SelectedTag>
          ))
        )}
      </SelectBox>

      {isOpen && (
        <Dropdown>
          <SearchInput
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search students... / 搜索学生..."
            autoFocus
          />
          {filteredStudents.length === 0 ? (
            <NoResults>
              {searchTerm
                ? 'No students match your search / 没有匹配的学生'
                : 'No available students in this campus / 该校区没有可用的学生'}
            </NoResults>
          ) : (
            filteredStudents.map(student => (
              <Option
                key={student.id}
                $selected={selectedIds.includes(student.id)}
                onClick={() => toggleStudent(student.id)}
              >
                <Checkbox
                  type="checkbox"
                  checked={selectedIds.includes(student.id)}
                  onChange={() => {}}
                />
                <StudentInfo>
                  <div className="name">{student.name}</div>
                  <div className="meta">
                    {student.schoolName && `${student.schoolName} `}
                    {student.grade !== undefined && student.grade !== null && getGradeLabel(student.grade)}
                  </div>
                </StudentInfo>
              </Option>
            ))
          )}
        </Dropdown>
      )}
    </Container>
  );
};

export default StudentMultiSelect;
